import {
  BillingCycle,
  Payment as PaymentType,
} from '@views/Invoice/invoiceTypes';

import { useEffect, useState } from 'react';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  HiOutlineCreditCard,
  HiArrowLeft,
  HiCheckCircle,
} from 'react-icons/hi';
import {
  Modal,
  ModalContainer,
  ModalContent,
  ModalHeader,
} from '@components/Modal';
import { Button } from '@components/Button';
import { useAuthHttp } from '../../hooks';
import { INVOICE_LIST } from '../../constants';
import { TransactionSummary } from './TransactionSummary';
import { PaymentDetails } from './PaymentDetails';
import { UserRoleService } from '../../services';
import { PageHeader } from '../../tailwindComponents/PageHeader';

export function Payment() {
  const authHttp = useAuthHttp();
  const navigate = useNavigate();
  const { isWarehouseClient } = UserRoleService();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const hasJustPaid = !!searchParams.get('justPaid') && isWarehouseClient;
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [payment, setPayment] = useState<PaymentType>();
  const [billingCycles, setBillingCycles] = useState<BillingCycle[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const pages = [
    { name: 'Home', href: '/', current: false },
    { name: 'Payments', href: '/payments', current: false },
    { name: 'Payment Details', href: `/payment/${id}`, current: true },
  ];

  const fetchBillingCycles = async () => {
    setIsLoading(true);
    try {
      const { data } = await authHttp.post(
        INVOICE_LIST,
        {
          statuses: ['Paid'], // TODO: Add payment pending status?
          paymentId: id,
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      const relevantBillingCycles: BillingCycle[] = [];
      if (data.billingCycles) {
        data.billingCycles.forEach((billingCycle: BillingCycle) => {
          // TODO: Temporary measure while BE query is not working.
          // When that is fixed, ListBillingCycles will only return billingCycles with an invoice containing this payment
          // So we can look at the first billingCycle and not need to loop
          const paymentMatch = billingCycle.floxToShipperInvoice.payments.find(
            (p: { id: string | undefined }) => p.id === id
          );

          if (paymentMatch) {
            setPayment(paymentMatch);
            relevantBillingCycles.push(billingCycle);
          }
        });
        setBillingCycles(relevantBillingCycles);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBillingCycles();
    if (hasJustPaid) {
      setIsConfirmationModalOpen(true);
    }
  }, []);

  if (isLoading) {
    return (
      <>
        <PageHeader header='Payment Details' pages={pages} />
        <main className='flex flex-col gap-6 p-4 lg:px-8 lg:pb-8'>
          <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-12'>
            <div className='flex flex-col items-center gap-3'>
              <FloxSpinner size='md' />
              <p className='text-gray-500 text-sm'>Loading payment...</p>
            </div>
          </div>
        </main>
      </>
    );
  }

  if (!payment) {
    return (
      <>
        <PageHeader header='Payment Details' pages={pages} />
        <main className='flex flex-col gap-6 p-4 lg:px-8 lg:pb-8'>
          <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-12'>
            <div className='flex flex-col items-center gap-4'>
              <div className='w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center'>
                <HiOutlineCreditCard className='w-8 h-8 text-gray-400' />
              </div>
              <div className='text-center'>
                <h3 className='text-lg font-semibold text-gray-900'>
                  Payment not found
                </h3>
                <p className='text-gray-500 text-sm mt-1'>
                  The payment you&apos;re looking for doesn&apos;t exist or you
                  don&apos;t have permission to view it.
                </p>
              </div>
              <button
                type='button'
                onClick={() => navigate('/payments')}
                className='mt-2 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors'
              >
                <HiArrowLeft className='w-4 h-4' />
                Back to Payments
              </button>
            </div>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <PageHeader header='Payment Details' pages={pages} />
      <main className='flex flex-col gap-6 p-4 lg:px-8 lg:pb-8'>
        <TransactionSummary payment={payment} />
        <PaymentDetails billingCycles={billingCycles} />
      </main>

      <Modal
        open={isConfirmationModalOpen}
        onOpenChange={setIsConfirmationModalOpen}
      >
        <ModalContainer>
          <ModalHeader title='Payment Confirmation' />
          <ModalContent>
            <div className='flex flex-col items-center gap-4 py-4'>
              <div className='w-12 h-12 bg-green-100 rounded-full flex items-center justify-center'>
                <HiCheckCircle className='w-8 h-8 text-green-600' />
              </div>
              <div className='text-center space-y-2'>
                <p className='text-gray-900 font-medium'>
                  Your payment has been submitted successfully.
                </p>
                <p className='text-gray-500 text-sm'>
                  It may take up to 3 working days to process your payment.
                </p>
              </div>
            </div>
            {/* TODO: Implement socket for real-time updates */}
            <div className='flex justify-end mt-4'>
              <Button
                variant='primary'
                size='lg'
                onClick={() => setIsConfirmationModalOpen(false)}
              >
                Close
              </Button>
            </div>
          </ModalContent>
        </ModalContainer>
      </Modal>
    </>
  );
}
